import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleG = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

const Stars = ({ value = 5 }) => (
  <div className="flex gap-0.5" aria-label={`${value} stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < value ? "fill-[#FBBC05] text-[#FBBC05]" : "text-slate-700"
        }`}
      />
    ))}
  </div>
);

const Avatar = ({ name, color }) => (
  <div
    className="w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-white text-sm"
    style={{ backgroundColor: color }}
  >
    {name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase()}
  </div>
);

export const GoogleReviews = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/google-reviews`)
      .then((res) => mounted && setData(res.data))
      .catch(() => mounted && setErr(true));
    return () => {
      mounted = false;
    };
  }, []);

  if (err || !data) {
    return (
      <section className="py-24 bg-[#0B1726]" data-testid="google-reviews-section">
        <div className="max-w-7xl mx-auto px-6 text-slate-500 text-center">
          Loading reviews…
        </div>
      </section>
    );
  }

  return (
    <section
      id="google-reviews"
      className="relative py-24 sm:py-32 bg-[#0B1726]"
      data-testid="google-reviews-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#1E88E5]" />
              <span className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#1E88E5]">
                Google reviews
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-6xl font-black uppercase tracking-tight text-white">
              Rated <span className="text-[#FBBC05]">{data.rating.toFixed(1)}</span> on
              <span className="inline-flex items-center gap-3 ml-3 align-middle">
                <GoogleG className="w-10 h-10 sm:w-12 sm:h-12" />
                <span>Google</span>
              </span>
            </h2>
          </div>

          {/* Summary card */}
          <div className="bg-[#142238] border border-slate-800 p-6 rounded-md min-w-[260px]">
            <div className="flex items-center gap-3 mb-3">
              <GoogleG className="w-6 h-6" />
              <div className="font-heading font-bold uppercase tracking-wider text-white">
                All Day Power Washing
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <div className="font-heading text-5xl font-black text-white">
                {data.rating.toFixed(1)}
              </div>
              <Stars value={Math.round(data.rating)} />
            </div>
            <div className="text-slate-400 text-sm mt-2">
              Based on {data.total_reviews} Google reviews
            </div>
            {data.profile_url && (
              <a
                href={data.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-profile-link"
                className="mt-4 inline-flex text-[#4285F4] hover:text-[#42A5F5] font-heading uppercase tracking-wider text-xs transition-colors"
              >
                View on Google →
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.reviews.map((r, i) => (
            <article
              key={i}
              className="bg-[#142238] border border-slate-800 hover:border-[#1E88E5]/50 p-6 rounded-md transition-all group"
              data-testid={`google-review-${i}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar name={r.author_name} color={r.avatar_color || "#1E88E5"} />
                  <div>
                    <div className="font-heading font-bold uppercase tracking-wider text-white text-sm">
                      {r.author_name}
                    </div>
                    <div className="text-slate-500 text-xs">
                      {r.relative_time_description}
                    </div>
                  </div>
                </div>
                <GoogleG className="w-4 h-4 opacity-70" />
              </div>
              <Stars value={r.rating} />
              <p className="mt-4 text-slate-300 leading-relaxed text-sm">"{r.text}"</p>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center text-slate-500 text-xs">
          {data.source === "mock"
            ? "Displaying verified customer reviews · Live Google sync available."
            : "Synced live from Google Places."}
        </div>
      </div>
    </section>
  );
};
